// @ts-nocheck
import * as React from "react";
import { ManualMobileShell, Section, Lead, Steps, Note, GoTo, ShotMobile, FlowStripMobile } from "./manual-shell";
import { supportPhone } from "./common";

// 보호자(guardian) 전용 사용자 매뉴얼 — 모바일(폰) 인앱 가이드.
// 진입점은 보호자 홈(/dashboard/guardian)의 "사용자 매뉴얼" 링크.
// 어르신 본인(citizen)은 1인칭 전용 매뉴얼(manual-elder.tsx · /m/elder-manual)을 쓰므로
// 이 매뉴얼은 보호자가 보호하는 어르신의 케어 현황을 확인하는 흐름만 다룬다.
// 셸/프리미티브는 manual-shell.tsx(ManualMobileShell·ShotMobile·FlowStripMobile)에서 가져온다.

// 실제 화면 스크린샷 경로 — playwright test e2e/manual-shots/guardian.gen.spec.ts 로
// 생성(public/manual-shots/guardian/, 세로 390×844 폰 캡처 · 보호자 계정 기준).
const S = (key) => `/manual-shots/guardian/${key}.png`;

const TOC = [
  { id: "start",    label: "시작하기" },
  { id: "home",     label: "홈" },
  { id: "schedule", label: "일정·평가" },
  { id: "manager",  label: "매니저·메시지" },
  { id: "notify",   label: "알림" },
  { id: "sos",      label: "응급 SOS" },
  { id: "apply",    label: "케어 신청" },
  { id: "me",       label: "내 정보" },
  { id: "flow",     label: "이용 흐름" },
  { id: "faq",      label: "FAQ" },
];

const INTRO = (
  <>
    이 매뉴얼은 보호자가 휴대폰으로 어르신의 케어 현황을 확인하는 방법을 화면 흐름에 맞춰 안내합니다.
    케어 신청부터 방문 일정·알림·담당 매니저 연락·별점 평가, 그리고 위급 상황의 <b>응급 SOS</b>까지 다룹니다.
    화면 하단의 <b>5개 탭(홈·일정·매니저·알림·마이)</b>으로 이동합니다.
  </>
);

function GuardianManual() {
  return (
    <ManualMobileShell
      nav="guardian"
      tab="me"
      homeHref="/dashboard/guardian"
      badge="GUARDIAN GUIDE · 보호자용"
      title="보호자 사용자 매뉴얼"
      revised="최종 개정 2026-06-18 · 보호자(guardian) 기준"
      intro={INTRO}
      toc={TOC}
    >
      <Section id="start" title="1. 시작하기">
        <Lead>보호자는 휴대폰 앱으로 어르신의 케어 현황을 확인합니다. 화면 하단 5개 탭으로 이동하며, 마이 화면의 '도움말'에서 언제든 이 매뉴얼로 돌아올 수 있습니다.</Lead>
        <Steps items={[
          "발급받은 계정으로 로그인합니다.",
          "화면 하단 5개 탭으로 이동합니다 — 홈 · 일정 · 매니저 · 알림 · 마이.",
          "처음이라면 케어 신청부터 시작합니다(7장).",
          "위급한 상황에는 응급 SOS로 즉시 도움을 요청합니다(6장).",
          "도움이 필요할 때는 마이(내 정보) 화면의 '사용자 매뉴얼'로 이 화면에 돌아옵니다.",
        ]} />
        <Note>보호자는 로그인 후 <b>홈(보호자 대시보드)</b>으로 진입합니다. 어르신 본인이 직접 쓰는 계정은 1인칭 전용 화면과 별도 안내서를 사용합니다.</Note>
        <GoTo links={[{ href: "/dashboard/guardian", label: "홈 열기" }]} />
      </Section>

      <Section id="home" title="2. 홈">
        <Lead>어르신의 현재 상태를 한눈에 봅니다. 어르신 위험 카드, 담당 매니저와 오늘 방문 예정, 최근 방문 이력이 표시됩니다.</Lead>
        <Steps items={[
          "어르신 카드에서 이름·나이·복약·케어 상태와 위험도를 확인합니다.",
          "담당 매니저 카드에서 오늘 방문 예정 시각(ETA)을 봅니다.",
          "최근 방문 목록에서 지난 방문 내역을 확인합니다.",
          "위급할 때는 응급 SOS 버튼으로 바로 도움을 요청합니다(6장).",
          "더 자세한 내용은 하단 탭(일정·매니저·알림)으로 이동해 봅니다.",
        ]} />
        <ShotMobile src={S("home")} alt="보호자 홈 화면" caption="홈 — 어르신 상태·담당 매니저·오늘 방문 (탭하면 원본 크기)" />
        <GoTo links={[{ href: "/dashboard/guardian", label: "홈 열기" }]} />
      </Section>

      <Section id="schedule" title="3. 일정 (방문 이력 · 별점 평가)">
        <Lead>하단 '일정' 탭에서 방문 타임라인과 활력징후 추세를 보고, 완료된 방문은 담당 매니저를 별점으로 평가합니다.</Lead>
        <Steps items={[
          "이번 주 요약(방문 횟수·시간·혈압)을 확인합니다.",
          "방문 타임라인에서 예정·완료된 방문을 시간순으로 봅니다.",
          "최근 활력징후(혈압·맥박·체온) 기록을 확인합니다.",
          "완료된 방문의 '별점 평가'를 눌러 1~5점과 한줄평을 남깁니다 — 보낸 평가는 매니저 화면에 반영됩니다.",
        ]} />
        <ShotMobile src={S("timeline")} alt="방문 이력 타임라인 화면" caption="일정 — 방문 타임라인·이번 주 요약·별점 평가" />
        <GoTo links={[{ href: "/m/timeline", label: "일정 열기" }]} />
      </Section>

      <Section id="manager" title="4. 매니저 · 메시지">
        <Lead>하단 '매니저' 탭에서 담당 케어매니저 정보를 보고, 전화 또는 메시지로 연락합니다.</Lead>
        <Steps items={[
          "담당 매니저 카드에서 이름·자격·소속·별점·현재 상태를 확인합니다.",
          "'전화 걸기'로 매니저에게 바로 연락합니다.",
          "'메시지'를 누르면 담당 매니저와의 대화 화면(/m/chat)이 열립니다 — 보낸 말풍선과 '읽음' 표시가 있는 양방향 채팅입니다.",
          "이 매니저와의 최근 방문 내역을 확인합니다.",
        ]} />
        <Note>아직 매니저가 배정되지 않았거나 매칭 제안 단계일 때는 전화·메시지가 비활성화됩니다. 알림함의 '메시지' 알림을 누르면 곧바로 이 대화 화면으로 들어갑니다(5장).</Note>
        <ShotMobile src={S("manager")} alt="담당 매니저 화면" caption="매니저 — 담당 매니저 정보·전화·메시지·최근 방문" />
        <ShotMobile src={S("chat")} alt="매니저 대화 화면" caption="메시지 — 매니저와의 양방향 대화 스레드" />
        <GoTo links={[{ href: "/m/guardian-manager", label: "매니저 열기" }, { href: "/m/chat", label: "메시지 열기" }]} />
      </Section>

      <Section id="notify" title="5. 알림">
        <Lead>하단 '알림' 탭에서 방문·위험·복약·메시지 알림을 확인합니다.</Lead>
        <Steps items={[
          "알림은 날짜별(오늘·어제·이전)로 묶여 표시됩니다.",
          "상단 필터(전체·위험·방문·복약·메시지)로 원하는 알림만 봅니다.",
          "'메시지' 알림을 누르면 해당 매니저와의 대화 화면으로 바로 이동합니다.",
          "'모두 읽음'으로 일괄 처리하거나, 읽은 알림을 삭제합니다.",
          "위험(critical) 알림은 어르신의 활력징후가 위험 범위일 때 자동으로 도착합니다.",
        ]} />
        <Note>알림 수신 여부는 마이(내 정보) → 알림 설정에서 <b>카테고리별로 켜고 끌 수 있습니다</b>(8장).</Note>
        <ShotMobile src={S("notify")} alt="알림함 화면" caption="알림 — 날짜별 목록·필터·모두 읽음" />
        <GoTo links={[{ href: "/m/notify", label: "알림 열기" }]} />
      </Section>

      <Section id="sos" title="6. 응급 SOS">
        <Lead>어르신의 낙상·실종·학대 의심 등 위급한 상황에서 즉시 도움을 요청합니다. 접수 즉시 긴급 케이스가 만들어지고 가까운 응급기관(소방·병원·보건소)이 자동으로 연결되며, 권역 운영자에게 끌 수 없는 긴급 알림이 전달됩니다.</Lead>
        <Steps items={[
          "상황 유형을 6종 중에서 선택합니다 — 낙상·외상 / 배회·실종 / 안부 미응답 / 긴급돌봄 필요 / 정신건강 위기 / 학대 의심.",
          "상황 설명을 적습니다.",
          "제출하면 긴급 케이스가 생성되고, 매칭된 응급기관과 연락처가 결과 화면에 표시됩니다.",
          "최근 SOS 내역에서 이전 요청과 처리 상태를 확인할 수 있습니다.",
        ]} />
        <Note>생명이 위급한 응급 상황이라면 SOS 접수와 별개로 <b>먼저 119</b>에 신고하세요. SOS는 같은 권역의 운영자·응급기관에 상황을 즉시 공유하기 위한 기능입니다.</Note>
        <ShotMobile src={S("sos")} alt="응급 SOS 화면" caption="응급 SOS — 유형 선택·상황 설명·응급기관 자동 연결" />
        <GoTo links={[{ href: "/m/sos", label: "응급 SOS 열기" }]} />
      </Section>

      <Section id="apply" title="7. 케어 신청">
        <Lead>어르신이 아직 케어를 받고 있지 않다면 케어 신청으로 시작합니다. 단계별 양식을 채우면 권역에서 검토 후 케이스가 만들어집니다.</Lead>
        <Steps items={[
          "신청자 정보(이름·연락처·어르신과의 관계)를 입력합니다.",
          "어르신 정보(이름·나이·건강 상태)를 입력하고, 지도에서 거주지 위치 핀을 지정합니다.",
          "가구 유형과 장기요양등급을 선택하고 건강보험 정보 활용에 동의합니다.",
          "상단 진행률을 채워 제출하면 접수번호가 발급됩니다.",
        ]} />
        <Note>신청 후에는 권역 관리자의 승인 → 케이스 생성 → 매니저 매칭 순서로 진행됩니다. 진행 상황은 알림으로 안내됩니다.</Note>
        <ShotMobile src={S("apply")} alt="케어 신청 화면" caption="케어 신청 — 단계별 양식·거주지 핀·진행률·접수번호" />
        <GoTo links={[{ href: "/m/apply", label: "케어 신청 열기" }]} />
      </Section>

      <Section id="me" title="8. 내 정보">
        <Lead>하단 '마이' 탭에서 프로필 사진과 계정 설정을 관리하고, 이 매뉴얼에 다시 들어옵니다.</Lead>
        <Steps items={[
          "프로필 사진을 등록하거나 변경합니다(JPG·PNG·WebP, 최대 5MB).",
          "이름·연락처를 편집하고 비밀번호를 변경합니다.",
          "알림 설정에서 카테고리별 수신 여부를 켜고 끕니다.",
          "'도움말 > 사용자 매뉴얼'로 이 화면에 다시 들어오고, 이용약관·개인정보처리방침을 확인합니다.",
          "더 이상 서비스를 이용하지 않는다면 화면 하단에서 회원 탈퇴하거나 로그아웃합니다.",
        ]} />
        <Note>회원 탈퇴는 본인 확인을 위해 <b>현재 비밀번호</b>를 입력해야 하며, 탈퇴 즉시 로그인이 차단되고 유예 기간 후 개인정보가 익명화됩니다.</Note>
        <ShotMobile src={S("me")} alt="내 정보 화면" caption="마이 — 프로필 사진·이름·비밀번호·알림 설정·도움말·탈퇴" />
        <GoTo links={[{ href: "/m/guardian-me", label: "내 정보 열기" }]} />
      </Section>

      <Section id="flow" title="9. 이용 흐름">
        <Lead>케어를 처음 시작하는 표준 흐름입니다. 썸네일을 탭하면 원본 화면이 열립니다.</Lead>
        <FlowStripMobile steps={[
          { src: S("apply"),    label: "케어 신청" },
          { src: S("notify"),   label: "알림 수신" },
          { src: S("home"),     label: "홈 확인" },
          { src: S("manager"),  label: "매니저 연락" },
          { src: S("chat"),     label: "메시지·평가" },
        ]} />
      </Section>

      <Section id="faq" title="10. 자주 묻는 질문 · 문의">
        <p style={{ margin: "10px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13 }}>Q. 알림이 오지 않아요.</p>
        <Lead>마이(내 정보) → 알림 설정에서 해당 카테고리(방문·위험·복약·메시지)가 켜져 있는지 확인하세요(5·8장).</Lead>
        <p style={{ margin: "12px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13 }}>Q. 매니저에게 어떻게 연락하나요?</p>
        <Lead>'매니저' 탭에서 전화를 걸거나 '메시지'로 대화 화면을 열어 글로 주고받을 수 있습니다(4장).</Lead>
        <p style={{ margin: "12px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13 }}>Q. 위급한 상황이에요.</p>
        <Lead>생명이 위급하면 먼저 119에 신고하고, 앱에서는 응급 SOS로 권역 운영자·응급기관에 상황을 즉시 공유하세요(6장).</Lead>
        <p style={{ margin: "12px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13 }}>Q. 케어 신청 후 어떻게 진행되나요?</p>
        <Lead>권역 관리자의 승인 → 케이스 생성 → 케어매니저 매칭 순으로 진행되며, 각 단계는 알림으로 안내됩니다(7장).</Lead>
        <p style={{ margin: "12px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13 }}>Q. 담당 매니저를 바꾸고 싶어요.</p>
        <Lead>매니저 배정·변경은 권역에서 결정합니다. 운영지원으로 요청해 주세요.</Lead>
        <div style={{ marginTop: 16, padding: 14, background: "var(--bg-sub)", borderRadius: 12, fontSize: 12, color: "var(--ink-2)", lineHeight: 1.7 }}>
          <div style={{ fontWeight: 800, color: "var(--ink-1)", marginBottom: 4 }}>운영지원 문의</div>
          support@care.local · {supportPhone()} (평일 09:00–18:00)
        </div>
      </Section>
    </ManualMobileShell>
  );
}

export { GuardianManual };
